<template></template>
<script>
import _ from 'lodash';
import Vue from 'vue';
import Vuex from 'vuex';
import V from 'voca';
import { plural, singular } from 'pluralize';

import Notifications from 'vue-notification';
import MoonLoader from 'vue-spinner/src/MoonLoader.vue';
import ContentViewComponent from './ContentViewComponent';
import ControlPanelComponent from './ControlPanelComponent';
import ContentToolbarComponent from './helpers/ContentToolbarComponent';
import ToplevelToolbarComponent from './helpers/ToplevelToolbarComponent';
import GlobalFileModalComponent from './helpers/GlobalFileModalComponent';

Vue.use(Notifications);

export default {
    data() {
        return {
            currentSection: null,
            currentSubSection: null,
            previousSection: null,
            relationship: null,
            sections: [],
            subSections: [],
			breadcrumb: [],
            localContent: {},
            localTimestamp: Date.now(),
            selected: [],
            contentErrors: {},
            dirty: false,

            forceFetch: false,
            haltFetch: false,
			initialized: false,
            componentName: null,
            componentConfig: {},
        }
    },

    components: {
        ContentViewComponent,
        ControlPanelComponent,
        ContentToolbarComponent,
        ToplevelToolbarComponent,
        GlobalFileModalComponent,
        VueSpinner: MoonLoader,
    },

    watch: {
        $route(to, from) {
            const toQuery = JSON.stringify(to.query);
            const fromQuery = JSON.stringify(from.query);

            if (this.haltFetch) {
                this.haltFetch = false;
                return;
            };
            if (this.forceFetch) {
                this.forceFetch = false;
                this.getData({
                    setNotification: true,
                });
            } else if (to.path !== from.path || toQuery !== fromQuery) {
                this.getData({
                    setNotification: true,
                });
            }
        },

        topic(newTopic) {
            const title = this.config('cmsTitle');
            document.title = `${newTopic} - ${title}`;
        },

        timestamp(ts) {
            const uriFormat = this.helpers('uriFormat');

            this.localContent = { ...this.content };
            this.localTimestamp = ts;
            this.dirty = false;
            this.initialized = true;

            if (!this.tabDisplay) {
                this.sections = [{ name: this.content.type, link: '#' }]
                this.currentSection = this.content.type;
                this.subSections = [{ name: this.content.type, link: '#' }]
                this.currentSubSection = this.content.type;
				this.breadcrumb = [{ name: V.capitalize(this.topic) }, { name: this.content.type }];
                return;
            }

            const { endpoint, id, relationship: relEndpoint, relationshipId: relId } = this.resourceParams;

            // change root tab when not query item or relationship
            if (!id && !relEndpoint) {
                this.currentSection = endpoint;
                this.currentSubSection = endpoint;

                const selfLink = uriFormat(this.content.links.self);

                this.sections = [
                    { label: 'All ' + V.capitalize(endpoint), name: endpoint, link: selfLink, active: true },
                ];
                this.subSections = [
                    { label: 'All ' + V.capitalize(endpoint), name: endpoint, link: selfLink, active: true },
                ];
				this.breadcrumb = [{ name: V.capitalize(this.topic) }, { name: endpoint }];
                this.relationship = null;

            } else if (id && !relEndpoint) {
                this.currentSection = endpoint;
                this.currentSubSection = endpoint;

                const collectionType = _.get(this.content, 'schema.collectionType');
                const selfLink = uriFormat(_.get(this.content, 'links.self') );
                const collectionLink = uriFormat(_.get(this.content, 'links.collection') );

                const endpointLabel = (id == '_') ? `New ${endpoint}` : V.capitalize(endpoint) + ' Details';
                this.sections = [
                    { label: 'All ' + V.capitalize(collectionType), name: collectionType, link: collectionLink },
                    { label: endpointLabel , name: endpoint, link: selfLink, active: true },
                ].concat(this.getRelationshipSections(this.content));
                this.subSections = [
                    { label: V.capitalize(endpoint) + ' Details', name: endpoint, link: selfLink, active: true },
                ];

                let breadcrumbName = `New ${this.content.type}`;
                let breadcrumbLabel = null;
                if (this.content.id) {
                    breadcrumbLabel = this.content.attributes.label;
                    breadcrumbName = this.content.attributes.name
                        || this.content.attributes.code
                        || `${this.content.type}#${this.content.id}`;
                }
                this.breadcrumb = [
                    { name: V.capitalize(this.topic) },
                    { name: collectionType, link: collectionLink },
                    { name: breadcrumbName, label: breadcrumbLabel },
                ];
                this.relationship = null;

            } else if (id && relEndpoint && !relId) {
                this.currentSection = relEndpoint;
                this.currentSubSection = relEndpoint;

                this.relationship = this.parent.schema.relationships[relEndpoint].type;

                const { collectionType: parentColType } = this.parent.schema;
                const parentLink = uriFormat(this.parent.links.self);
                const parentCollectionLink = uriFormat(this.parent.links.collection);

                this.sections = [
                    { label: 'All ' + V.capitalize(parentColType), name: parentColType, link: parentCollectionLink },
                    { label: V.capitalize(endpoint) + ' Details', name: endpoint, link: parentLink },
                ]   .concat(this.getRelationshipSections(this.parent))
                    .map(section => ({ ...section, active: section.name == relEndpoint }));

                const selfLink = uriFormat(this.content.links.self);
                const relType = this.content.type;
                this.subSections = [
                    { label: 'All ' + V.capitalize(relType), name: relType, link: selfLink, relationship: this.relationship, active: true },
                ].concat(this.getRelationshipSections(this.content))

                const parentBreadcrumbLabel = this.parent.attributes.label;
                const parentBreadcrumbName = this.parent.attributes.name
                    || this.parent.attributes.code
                    || `${this.parent.type}#${this.parent.id}`;
                this.breadcrumb = [
                    { name: V.capitalize(this.topic) },
                    { name: parentColType, link: parentCollectionLink },
                    { name: parentBreadcrumbName, link: parentLink, label: parentBreadcrumbLabel },
                    { name: relEndpoint },
                ];

            } else {
                const { collectionType: parentColType } = this.parent.schema;
                const parentLink = uriFormat(this.parent.links.self);
                const parentCollectionLink = uriFormat(this.parent.links.collection);

                this.sections = [
                    { label: 'All ' + V.capitalize(parentColType), name: parentColType, link: parentCollectionLink},
                    { label: V.capitalize(endpoint) + ' Details', name: endpoint, link: parentLink },
                ]   .concat(this.getRelationshipSections(this.parent))
                    .map(section => ({ ...section, active: _.singular(section.name) == relEndpoint }));

                const { collectionType: relCollectionType } = this.content.schema;
                const relType = this.content.type;
                const selfLink = uriFormat(this.content.links.self);
                const collectionLink = uriFormat(this.content.links.collection);

                this.currentSection = relCollectionType;
                this.currentSubSection = this.content.type;

                const endpointLabel = (relId == '_') ? `New ${relEndpoint}` : V.capitalize(relEndpoint) + ' Details';
                this.subSections = [
                    { label: 'All ' + V.capitalize(relCollectionType), name: relCollectionType, link: collectionLink, relationship: this.relationship, },
                    { label: endpointLabel, name: this.content.type, link: selfLink, relationship: this.relationship, active: true },
                ].concat(this.getRelationshipSections(this.content))

                const parentBreadcrumbLabel = this.parent.attributes.label;
                const parentBreadcrumbName = this.parent.attributes.name
                    || this.parent.attributes.code
                    || `${this.parent.type}#${this.parent.id}`;

                let breadcrumbName = `New ${this.content.type}`;
                let breadcrumbLabel = null;
                if (this.content.id) {
                    breadcrumbLabel = this.content.attributes.label;
                    breadcrumbName = this.content.attributes.name
                        || this.content.attributes.code
                        || `${this.content.type}#${this.content.id}`;
                }
				this.breadcrumb = [
                    { name: V.capitalize(this.topic) },
                    { name: parentColType, link: parentCollectionLink },
                    { name: parentBreadcrumbName, link: parentLink, label: parentBreadcrumbLabel },
                    { name: relCollectionType, link: collectionLink },
                    { name: breadcrumbName, link: selfLink, label: breadcrumbLabel }
                ];
            }
        },

        notifications(newVal) {
            const not = _.last(newVal);
            this.$notify({
                group: 'notification',
                title: not.type == 'error' ? 'Warning' : 'Message',
                type: not.type == 'error' ? 'warn' : 'success',
                text: _.isArray(not.message) ? not.message.join('.</br>') : not.message,
                duration: 3000,
            })
        },
    },

    computed: {
        ...Vuex.mapGetters({
            content: 'content',
            parent: 'parent',
            user: 'user',
			topic: 'topic',
            notifications: 'notifications',
            resourceParams: 'resourceParams',
            timestamp: 'timestamp',
            route: 'route',
            path: 'path',
            query: 'query',
            section: 'section',
            loading: 'loading',
        }),

        contentType() {
            return _.has(this.localContent, 'data') ? 'collection' : 'item';
        },

        topLevelContent() {
            return this.parent || this.content;
        },

        topLevelType() {
            return singular(_.chain(this.topLevelContent).get('type').split('.').first().value());
        },

        topLevelConfig() {
            const type = this.topLevelType;
            const topConfig = this.config(`collection.${plural(type)}`) || {};
            return topConfig;
        },

        isRelationship() {
            return !!this.resourceParams.relationship;
        },

        openControlPanel() {
            switch (this.componentName) {
                case 'table-component':
                    return _.size(this.selected);
                case 'form-component':
                    return false;
                default:
                    return false;
            }
        },

        headerDisplay() {
            // return ['dashboard', 'assets', 'error', 'message'].indexOf(this.content.type) < 0;
            // temporarily disable header
            return false;
        },

        breadcrumbDisplay() {
            return ['dashboard', 'assets', 'error', 'message'].indexOf(this.content.type) < 0;
        },

        tabDisplay() {
            return ['dashboard', 'assets', 'error'].indexOf(this.content.type) < 0;
        },

        fulltype() {
            let type = this.content.type;
            if (this.parent) {
                type = `${this.parent.type}.${type}`;
            }

            return type;
        },

        resourcePath() {
            return _.chain(this.$route)
                .get('params')
                .pick(['endpoint', 'relationship'])
                .values()
                .filter()
                .join('.')
                .value();
        },

        parentConfig() {
            const parentType = _.get(this.parent, 'type');
            return this.config(`form.${parentType}`) || {};
        },

        componentMapping() {
            return this.config('componentMapping') || {};
        },
    },

    mounted() {
        console.log('Component mounted.')
        const title = this.config('cmsTitle');

        document.title = `${this.topic} - ${title}`;
    },

    methods: {
        ...Vuex.mapActions([
            'changeSection', 'changeQuery', 'changeUrl', 'changePath',
            'getData', 'createData', 'updateData',  'updateBatch', 'deleteData', 'replaceData',
            'updateParent', 'deleteParent', 'setParent', 'setContent',
            'addNotification', 'removeNotification', 'commitQueue', 'pushQueue',
        ]),

        // modify top level API methods so that
        // they accept multiple arguments instead of just 1
        modifiedCreateData(data, options) {
            this.$Progress.start();
            return this.createData({ data, options })
                .then((res) => {
                    if (res) {
                        this.$Progress.finish();
                    } else {
                        this.$Progress.fail();
                    }
                });
        },

        modifiedUpdateData(data, options) {
            this.$Progress.start();
            return this.updateData({ data, options })
                .then((res) => {
                    if (res) {
                        this.$Progress.finish();
                    } else {
                        this.$Progress.fail();
                    }
                });
        },

        modifiedUpdateBatch(data, options) {
            this.$Progress.start();
            return this.updateBatch({ data, options })
                .then((res) => {
                    this.$Progress.finish();
                });
        },

        changeTab(section) {
            if (section.name == this.currentSection) return;
            this.relationship = null;
            this.currentSection = section.name;
            this.currentSubSection = section.name;

            if (section.link.match(/^\#/)) {
                this.changeSection(section.link);
                this.forceFetch = false;
            } else {
                this.changeUrl(section.link);
                this.forceFetch = true;
            }
        },

        changeSubTab(subSection) {
            if (subSection.name == this.currentSubSection) return;
            this.relationship = null;
            this.currentSubSection = subSection.name;

            if (subSection.link.match(/^\#/)) {
                this.changeSection(subSection.link);
                this.forceFetch = false;
            } else {
                this.changeUrl(subSection.link);
                this.forceFetch = true;
            }
        },

        getRelationshipSections(content) {
            let relationshipSections;
            try {
                const relationships = content.schema.relationships;
                const links = content.links;

                relationshipSections = _.keys(relationships)
                    .map(name => {
                        const link = links[name];
                        const type = _.chain(relationships).get([name, 'class'], '').snakeCase().value();
                        const section = { name, type, link, relationship: relationships[name].type };
                        return section;
                    })
                    .filter(section => section.link && section.relationship !== 'BelongsTo');

            } catch (e) {
                relationshipSections = [];
            }

            return relationshipSections;
        },

        // loading new instance instead
        loadNewInstance(payload) {
            const { type } = payload;
            let url = _.get(this.content, 'links.new', '');
            return this.changeUrl(url);
        },

        // this section perform the local update to show the create form
        showCreateForm(payload) {
            const { type, schema, attributes, meta } = payload;

            this.localContent = payload;

            if (this.relationship) {
                this.currentSubSection = type;
                this.subSections = this.subSections
                    .map(subSection => ({ ...subSection }))
                    .concat([{
                        name: type,
                        link: '#create',
                    }]);
            } else {
                this.currentSection = type;
                this.sections = this.sections
                    .map(section => ({ ...section }))
                    .concat([{
                        name: type,
                        link: '#create',
                    }]);
            }
            this.changeSection('create');
        },

        showCreateFormParent() {
            const newContent = this.cloneContent(this.parent);

            this.haltFetch = true;
            this.changeUrl(newContent.links.self);
            this.changeSection('create');
            this.setContent(newContent);
        },

        // this function will always create top level content
        showCreateFormCurrent() {
            const newContent = this.cloneContent(this.content);

            this.haltFetch = true;
            this.changeUrl(newContent.links.self);
            this.changeSection('create');
            this.setContent(newContent);
        },

        showCreateFormTopLevel() {
            if (this.parent) {
                return this.showCreateFormParent();
            }
            return this.showCreateFormCurrent();
        },

        cloneContent(content) {
            const { type, schema } = content;

            if (content.data) {
                return { id: null, type: schema.instanceType, schema, attributes: {}, meta: {}, links: { self: content.links.self } };
            }
            return { ...content, id: null, attributes: {}, meta: {}, links: { self: content.links.collection } };
        },

        updateLocalContent(payload) {
            this.localContent.attributes = { ...this.localContent.attributes, ...payload.data };
            this.localContent.updateBatch = {  ...payload.updateBatch };
            this.contentErrors = payload.errors;

            this.dirty = true;
            this.localTimestamp = Date.now();
        },
        // end local form section

        resetLocalState() {
            this.dirty = false;
        },

        updateSelected(payload) {
            const { data } = payload;
            this.selected = data;
            this.cpSwitch = true;
        },

        doNothing(payload) {
            // const { type, data } = payload;
            // do nothing
        },

        backToCollection(payload) {
            this.changeUrl(payload.links.collection);
        },

        changeUrlForSubSection(val) {
            this.changeUrl(val);
        },

        changeBreadcrumbLink(link) {
            this.relationship = null;
            this.changeUrl(link);
        },

        getComponentForType(type) {
            return _.get(this.componentMapping[type], 'component');
        },

        isSectionEnabled(type, section) {
            return _.get(this.componentMapping[type], 'sections', [section])
                .indexOf(section) >= 0;
        },

        getConfigForType(type) {
            return this.componentMapping[type] || {};
        },

        updateComponent(component) {
            this.initialized = true;
            this.componentName = component.name;
            this.componentConfig = component.config;
        },

        signalHaltFetch() {
            this.haltFetch = true;
        },
    },

    created() {
        this.$store.dispatch('getInitialData')
    }
}
</script>



// WEBPACK FOOTER //
// seventytwo/72cms-ui/src/components/BaseContentComponent.vue